<template>
  <div class="environment-alarm-page">
    <div class="page-title">环保报警信息</div>

    <div v-loading="overviewLoading" class="alarm-summary">
      <div v-for="item in summaryItems" :key="item.key" class="summary-item">
        <div class="summary-label">{{ item.label }}</div>
        <div class="summary-value">{{ item.value }}</div>
      </div>
    </div>

    <el-alert v-if="overviewError" class="state-alert" type="warning" :closable="false" show-icon>
      <span slot="title">{{ overviewError }}</span>
    </el-alert>

    <avue-crud
      ref="crud"
      v-model="form"
      :option="option"
      :table-loading="loading"
      :data="data"
      :page.sync="page"
      :search.sync="query"
      :defaults.sync="defaults"
      @search-change="searchChange"
      @search-reset="searchReset"
      @refresh-change="refreshChange"
      @current-change="currentChange"
      @size-change="sizeChange"
      @selection-change="selectionChange"
      @on-load="onLoad"
      @before-open="beforeOpen"
    >
      <template slot="sourceType" slot-scope="{ row }">
        {{ sourceLabel(row.sourceType) }}
      </template>
      <template slot="roomName" slot-scope="{ row }">
        {{ roomText(row) }}
      </template>
      <template slot="pointName" slot-scope="{ row }">
        {{ pointText(row) }}
      </template>
      <template slot="alarmType" slot-scope="{ row }">
        {{ alarmTypeLabel(row.alarmType) }}
      </template>
      <template slot="alertValue" slot-scope="{ row }">
        {{ valueText(alarmValue(row), row.unit) }}
      </template>
      <template slot="thresholdValue" slot-scope="{ row }">
        {{ valueText(row.thresholdValue, row.unit) }}
      </template>
      <template slot="alertStatus" slot-scope="{ row }">
        <el-tag size="small" :type="statusTagType(row.alertStatus)">
          {{ statusLabel(row.alertStatus) }}
        </el-tag>
      </template>
      <template slot="repairWorkOrderNo" slot-scope="{ row }">
        <el-link v-if="row.repairWorkOrderNo" type="primary" :underline="false" @click="openWorkOrder(row)">
          {{ row.repairWorkOrderNo }}
        </el-link>
        <span v-else>-</span>
      </template>
      <template slot="menu" slot-scope="{ row }">
        <el-button
          v-if="hasPermission('power_room_alarm_view')"
          type="text"
          size="small"
          icon="el-icon-view"
          @click="openDetail(row)"
        >查看</el-button>
        <el-button
          v-if="hasPermission('power_room_alarm_handle') && canConfirm(row)"
          type="text"
          size="small"
          icon="el-icon-check"
          @click="openProcessDialog(row, 'PROCESSING')"
        >确认</el-button>
        <el-button
          v-if="hasPermission('power_room_alarm_handle') && canHandle(row)"
          type="text"
          size="small"
          icon="el-icon-setting"
          @click="openProcessDialog(row, 'HANDLED')"
        >处理</el-button>
        <el-button
          v-if="hasPermission('power_room_alarm_handle') && canClose(row)"
          type="text"
          size="small"
          icon="el-icon-circle-close"
          class="danger-text"
          @click="openProcessDialog(row, 'CLOSED')"
        >关闭</el-button>
        <el-button
          v-if="hasPermission('power_room_alarm_work_order_submit') && canSubmitWorkOrder(row)"
          type="text"
          size="small"
          icon="el-icon-s-order"
          @click="openWorkOrderDialog(row)"
        >提交维修工单</el-button>
        <el-button
          v-if="row.repairWorkOrderNo"
          type="text"
          size="small"
          icon="el-icon-document"
          @click="openWorkOrder(row)"
        >查看工单</el-button>
      </template>
    </avue-crud>

    <dialog-reset slotShow height="72vh" top="6vh" width="76%" title="环保报警信息详情" :visible.sync="detailVisible">
      <div v-loading="detailLoading" class="detail-body">
        <template v-if="detailRecord.id">
          <div class="detail-section">
            <div class="section-title">基础信息</div>
            <div class="detail-grid">
              <div v-for="item in detailFields" :key="item.label" class="detail-item" :class="{ 'detail-item--wide': item.wide }">
                <span class="detail-item__label">{{ item.label }}</span>
                <span class="detail-item__value">{{ item.value || '-' }}</span>
              </div>
            </div>
          </div>

          <div class="detail-actions">
            <el-button
              v-if="hasPermission('power_room_alarm_handle') && canProcess(detailRecord)"
              type="primary"
              size="small"
              icon="el-icon-setting"
              @click="openProcessDialog(detailRecord, nextStatus(detailRecord))"
            >处理报警</el-button>
            <el-button
              v-if="hasPermission('power_room_alarm_work_order_submit') && canSubmitWorkOrder(detailRecord)"
              type="success"
              size="small"
              icon="el-icon-s-order"
              @click="openWorkOrderDialog(detailRecord)"
            >提交维修工单</el-button>
          </div>

          <div class="detail-section">
            <div class="section-title">处理时间线</div>
            <el-timeline v-if="timeline.length">
              <el-timeline-item
                v-for="(node, index) in timeline"
                :key="index"
                :timestamp="node.processTime || node.createTime || node.handleTime || ''"
                placement="top"
                :type="timelineType(node)"
              >
                <div class="timeline-node">
                  <div class="timeline-node__title">{{ statusLabel(node.targetStatus || node.alertStatus || node.status) }}</div>
                  <div class="timeline-node__meta">{{ node.processorName || node.handleUserName || node.createUserName || '-' }}</div>
                  <div class="timeline-node__content">{{ node.processResult || node.content || node.remark || '-' }}</div>
                  <div v-if="imageList(node.imageUrls || node.imagePath).length" class="image-list">
                    <el-image
                      v-for="(url, idx) in imageList(node.imageUrls || node.imagePath)"
                      :key="url + idx"
                      class="evidence-image"
                      :src="url"
                      :preview-src-list="imageList(node.imageUrls || node.imagePath)"
                      fit="cover"
                    >
                      <div slot="error" class="image-slot"><i class="el-icon-picture-outline"></i></div>
                    </el-image>
                  </div>
                </div>
              </el-timeline-item>
            </el-timeline>
            <el-empty v-else description="暂无处理记录" />
          </div>

          <div class="detail-section">
            <div class="section-title">维修工单信息</div>
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-item__label">关联工单</span>
                <span class="detail-item__value">{{ detailRecord.repairWorkOrderNo || detailRecord.workOrderNo || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-item__label">工单状态</span>
                <span class="detail-item__value">{{ detailRecord.workOrderStatus || '-' }}</span>
              </div>
            </div>
          </div>
        </template>
        <el-empty v-else-if="!detailLoading" description="暂无详情数据" />
      </div>
    </dialog-reset>

    <dialog-reset slotShow height="50vh" top="12vh" width="640px" :title="processDialogTitle" :visible.sync="processVisible">
      <el-form ref="processForm" :model="processForm" :rules="processRules" label-width="110px">
        <el-form-item label="目标状态" prop="targetStatus">
          <el-select v-model="processForm.targetStatus" placeholder="请选择目标状态">
            <el-option v-for="item in processStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="处理结果" prop="processResult">
          <el-input v-model.trim="processForm.processResult" type="textarea" :rows="4" maxlength="500" show-word-limit placeholder="请输入处理结果" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model.trim="processForm.remark" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="请输入备注" />
        </el-form-item>
        <el-form-item label="附件地址" prop="imageUrls">
          <el-input v-model.trim="processForm.imageUrls" type="textarea" :rows="2" maxlength="1000" placeholder="多个附件地址用英文逗号分隔" />
        </el-form-item>
      </el-form>
      <span slot="dialog_footer">
        <el-button @click="processVisible = false">取消</el-button>
        <el-button type="primary" :loading="actionLoading" @click="submitProcess">提交</el-button>
      </span>
    </dialog-reset>

    <dialog-reset slotShow height="58vh" top="8vh" width="680px" title="提交维修工单" :visible.sync="workOrderVisible">
      <el-form ref="workOrderForm" :model="workOrderForm" :rules="workOrderRules" label-width="120px">
        <el-form-item label="故障地点" prop="faultLocation">
          <el-input v-model.trim="workOrderForm.faultLocation" maxlength="100" show-word-limit placeholder="请输入故障地点" />
        </el-form-item>
        <el-form-item label="故障描述" prop="faultDescription">
          <el-input v-model.trim="workOrderForm.faultDescription" type="textarea" :rows="4" maxlength="1000" show-word-limit placeholder="请输入故障描述" />
        </el-form-item>
        <el-form-item label="优先级" prop="priority">
          <el-select v-model="workOrderForm.priority" placeholder="请选择优先级">
            <el-option label="低" value="LOW" />
            <el-option label="中" value="MEDIUM" />
            <el-option label="高" value="HIGH" />
            <el-option label="紧急" value="URGENT" />
          </el-select>
        </el-form-item>
        <el-form-item label="附件地址" prop="imagePath">
          <el-input v-model.trim="workOrderForm.imagePath" type="textarea" :rows="2" maxlength="1000" placeholder="多个附件地址用英文逗号分隔" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model.trim="workOrderForm.remark" type="textarea" :rows="3" maxlength="500" show-word-limit placeholder="请输入备注" />
        </el-form-item>
      </el-form>
      <span slot="dialog_footer">
        <el-button @click="workOrderVisible = false">取消</el-button>
        <el-button type="primary" :loading="actionLoading" @click="submitWorkOrderForm">提交</el-button>
      </span>
    </dialog-reset>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import crudMixins from '@/mixins/crud-mixins'
import { searchTimeConfig } from '@/basic-common/util/common'
import { detail, overview, page as pageAlarm, process as processAlarm, submitWorkOrder } from './api'

const SOURCE_OPTIONS = [
  { label: '配电室环境', value: 'POWER_ROOM' },
  { label: '煤棚环保', value: 'COAL_SHED' }
]

const ALARM_TYPE_OPTIONS = [
  { label: '温度报警', value: 'TEMPERATURE' },
  { label: '湿度报警', value: 'HUMIDITY' },
  { label: '烟感报警', value: 'SMOKE' },
  { label: '瓦斯报警', value: 'GAS' },
  { label: '一氧化碳报警', value: 'CO' },
  { label: '其他', value: 'OTHER' }
]

const STATUS_OPTIONS = [
  { label: '待确认', value: 'PENDING', tag: 'warning', aliases: ['1', 'WAIT_CONFIRM', 'WAITING', 'UNCONFIRMED'] },
  { label: '处理中', value: 'PROCESSING', tag: 'primary', aliases: ['2', 'CONFIRMED', 'HANDLING'] },
  { label: '已处理', value: 'HANDLED', tag: 'success', aliases: ['3', 'PROCESSED', 'DONE'] },
  { label: '已关闭', value: 'CLOSED', tag: 'info', aliases: ['4', 'CLOSE'] }
]

function responseData(res) {
  const body = res && res.data !== undefined ? res.data : res
  if (body && body.data !== undefined) return body.data
  return body || {}
}

function responseSuccess(res) {
  const body = res && res.data !== undefined ? res.data : res
  return !body || body.code === undefined || Number(body.code) === 200
}

function responseMessage(res, fallback) {
  const body = res && res.data !== undefined ? res.data : res
  return (body && (body.msg || body.message)) || fallback
}

export default {
  name: 'basic-info-environment-alarm',
  mixins: [crudMixins],
  data() {
    return {
      apiConfig: { getList: pageAlarm, getDetail: detail },
      overviewLoading: false,
      overviewError: '',
      overviewData: {},
      detailVisible: false,
      detailLoading: false,
      detailData: {},
      detailRecord: {},
      processVisible: false,
      processRow: null,
      processForm: this.emptyProcessForm(),
      processRules: {
        targetStatus: [{ required: true, message: '请选择目标状态', trigger: 'change' }],
        processResult: [{ required: true, message: '请输入处理结果', trigger: 'blur' }]
      },
      workOrderVisible: false,
      workOrderRow: null,
      workOrderForm: this.emptyWorkOrderForm(),
      workOrderRules: {
        faultLocation: [{ required: true, message: '请输入故障地点', trigger: 'blur' }],
        faultDescription: [{ required: true, message: '请输入故障描述', trigger: 'blur' }],
        priority: [{ required: true, message: '请选择优先级', trigger: 'change' }]
      },
      actionLoading: false,
      processStatusOptions: STATUS_OPTIONS.filter(item => item.value !== 'PENDING'),
      option: {
        ...this.$store.state.publicOption,
        menu: true,
        addBtn: false,
        delBtn: false,
        editBtn: false,
        viewBtn: false,
        selection: false,
        menuWidth: 320,
        searchMenuSpanSuper: 6,
        column: [
          {
            label: '报警来源',
            prop: 'sourceType',
            type: 'select',
            search: true,
            slot: true,
            dicData: SOURCE_OPTIONS,
            props: { label: 'label', value: 'value' },
            dataType: 'string',
            width: 120
          },
          {
            label: '配电室/煤棚',
            prop: 'roomCode',
            search: true,
            hide: true,
            type: 'input',
            searchPlaceholder: '请输入编码'
          },
          {
            label: '配电室/煤棚',
            prop: 'roomName',
            slot: true,
            minWidth: 160,
            overHidden: true
          },
          {
            label: '点位/指标',
            prop: 'pointName',
            slot: true,
            minWidth: 180,
            overHidden: true
          },
          {
            label: '报警类型',
            prop: 'alarmType',
            type: 'select',
            search: true,
            slot: true,
            dicData: ALARM_TYPE_OPTIONS,
            props: { label: 'label', value: 'value' },
            dataType: 'string',
            width: 120
          },
          {
            label: '报警值',
            prop: 'alertValue',
            slot: true,
            minWidth: 120
          },
          {
            label: '阈值',
            prop: 'thresholdValue',
            slot: true,
            minWidth: 120
          },
          {
            label: '单位',
            prop: 'unit',
            width: 80,
            align: 'center'
          },
          {
            label: '报警时间',
            prop: 'alertTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            minWidth: 170
          },
          {
            label: '报警状态',
            prop: 'alertStatus',
            type: 'select',
            search: true,
            slot: true,
            dicData: STATUS_OPTIONS.map(item => ({ label: item.label, value: item.value })),
            props: { label: 'label', value: 'value' },
            dataType: 'string',
            width: 110
          },
          {
            label: '处理人',
            prop: 'processorName',
            width: 110,
            overHidden: true
          },
          {
            label: '处理时间',
            prop: 'processTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            minWidth: 170
          },
          {
            label: '关联工单',
            prop: 'repairWorkOrderNo',
            slot: true,
            minWidth: 150,
            overHidden: true
          },
          {
            ...searchTimeConfig,
            label: '报警时间',
            paramsKey: ['startTime', 'endTime'],
            search: true
          }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['permission']),
    summaryItems() {
      return [
        { key: 'total', label: '报警总数', value: this.countText(this.overviewData.total) },
        { key: 'pending', label: '待确认', value: this.countText(this.overviewData.pending) },
        { key: 'processing', label: '处理中', value: this.countText(this.overviewData.processing) },
        { key: 'today', label: '今日报警', value: this.countText(this.overviewData.today) },
        { key: 'latestAlertTime', label: '最新报警时间', value: this.overviewData.latestAlertTime || '-' }
      ]
    },
    timeline() {
      const data = this.detailData || {}
      return data.timeline || data.processTimeline || data.processList || data.records || []
    },
    detailFields() {
      const row = this.detailRecord || {}
      return [
        { label: '报警来源', value: this.sourceLabel(row.sourceType) },
        { label: '配电室/煤棚', value: this.roomText(row) },
        { label: '点位/指标', value: this.pointText(row) },
        { label: '报警类型', value: this.alarmTypeLabel(row.alarmType) },
        { label: '报警值', value: this.valueText(this.alarmValue(row), row.unit) },
        { label: '阈值', value: this.valueText(row.thresholdValue, row.unit) },
        { label: '报警状态', value: this.statusLabel(row.alertStatus) },
        { label: '报警时间', value: row.alertTime },
        { label: '处理人', value: row.processorName },
        { label: '处理时间', value: row.processTime },
        { label: '处理结果', value: row.processResult, wide: true },
        { label: '备注', value: row.remark, wide: true }
      ]
    },
    processDialogTitle() {
      return `${this.statusLabel(this.processForm.targetStatus)}报警`
    }
  },
  methods: {
    emptyProcessForm() {
      return {
        id: '',
        targetStatus: 'PROCESSING',
        processResult: '',
        remark: '',
        imageUrls: ''
      }
    },
    emptyWorkOrderForm() {
      return {
        id: '',
        faultLocation: '',
        faultDescription: '',
        priority: 'MEDIUM',
        remark: '',
        imagePath: ''
      }
    },
    initCardData() {
      this.loadOverview()
    },
    hasPermission(code) {
      return !this.permission || this.permission[code] !== false
    },
    buildQuery() {
      return this.queryData || this.formatParams(Object.assign({}, this.defaultParams, this.query))
    },
    loadOverview() {
      this.overviewLoading = true
      this.overviewError = ''
      overview(this.buildQuery()).then(res => {
        this.overviewData = responseData(res)
      }).catch(e => {
        console.warn(e)
        this.overviewData = {}
        this.overviewError = (e && e.message) || '统计信息加载失败'
      }).finally(() => {
        this.overviewLoading = false
      })
    },
    refreshList() {
      this.onLoad(this.page, this.query)
    },
    countText(value) {
      return value === null || value === undefined || value === '' ? 0 : value
    },
    findOption(options, value) {
      const text = String(value || '')
      return options.find(item => String(item.value) === text || (item.aliases || []).map(String).indexOf(text) > -1)
    },
    sourceLabel(value) {
      const option = this.findOption(SOURCE_OPTIONS, value)
      return option ? option.label : (value || '-')
    },
    alarmTypeLabel(value) {
      const option = this.findOption(ALARM_TYPE_OPTIONS, value)
      return option ? option.label : (value || '-')
    },
    statusLabel(value) {
      const option = this.findOption(STATUS_OPTIONS, value)
      return option ? option.label : (value || '-')
    },
    statusTagType(value) {
      const option = this.findOption(STATUS_OPTIONS, value)
      return option ? option.tag : 'info'
    },
    normalizeStatus(value) {
      const option = this.findOption(STATUS_OPTIONS, value)
      return option ? option.value : String(value || '')
    },
    roomText(row) {
      return row.roomName || row.roomCode || row.locationName || row.areaName || '-'
    },
    pointText(row) {
      const point = row.pointName || row.pointCode || row.indicatorName || row.indicatorCode || row.indexName
      return point || '-'
    },
    valueText(value, unit) {
      if (value === null || value === undefined || value === '') return '-'
      return unit ? `${value} ${unit}` : String(value)
    },
    firstValue(row, fields) {
      if (!row) return ''
      for (let i = 0; i < fields.length; i += 1) {
        const value = row[fields[i]]
        if (value !== null && value !== undefined && value !== '') return value
      }
      return ''
    },
    alarmValue(row) {
      return this.firstValue(row, ['currentValueText', 'currentValue', 'alertValue'])
    },
    isClosed(row) {
      return this.normalizeStatus(row && row.alertStatus) === 'CLOSED'
    },
    canProcess(row) {
      return row && row.id && !this.isClosed(row)
    },
    canConfirm(row) {
      return this.canProcess(row) && this.normalizeStatus(row.alertStatus) === 'PENDING'
    },
    canHandle(row) {
      return this.canProcess(row) && ['PENDING', 'PROCESSING'].indexOf(this.normalizeStatus(row.alertStatus)) > -1
    },
    canClose(row) {
      return this.canProcess(row) && ['PROCESSING', 'HANDLED'].indexOf(this.normalizeStatus(row.alertStatus)) > -1
    },
    canSubmitWorkOrder(row) {
      return this.canProcess(row) && !row.repairWorkOrderNo && !row.repairOrderId && !row.workOrderId
    },
    nextStatus(row) {
      const status = this.normalizeStatus(row && row.alertStatus)
      if (status === 'PENDING') return 'PROCESSING'
      if (status === 'PROCESSING') return 'HANDLED'
      return 'CLOSED'
    },
    openDetail(row) {
      if (!row || !row.id) {
        this.$message.warning('缺少报警记录ID')
        return
      }
      this.detailVisible = true
      this.detailLoading = true
      this.detailData = {}
      this.detailRecord = { ...row }
      detail(row.id).then(res => {
        if (!responseSuccess(res)) {
          this.$message.error(responseMessage(res, '加载详情失败'))
          return
        }
        const data = responseData(res)
        this.detailData = data || {}
        this.detailRecord = data.alarm || data.record || data.detail || { ...row, ...data }
      }).catch(e => {
        console.warn(e)
        this.$message.error((e && e.message) || '加载详情失败')
      }).finally(() => {
        this.detailLoading = false
      })
    },
    openProcessDialog(row, targetStatus) {
      if (!this.canProcess(row)) {
        this.$message.warning('该报警不能继续处理')
        return
      }
      this.processRow = row
      this.processForm = {
        ...this.emptyProcessForm(),
        id: row.id,
        targetStatus: targetStatus || this.nextStatus(row)
      }
      this.processVisible = true
      this.$nextTick(() => this.$refs.processForm && this.$refs.processForm.clearValidate())
    },
    submitProcess() {
      this.$refs.processForm.validate(valid => {
        if (!valid) return
        const payload = {
          id: this.processForm.id,
          targetStatus: this.processForm.targetStatus,
          processResult: this.processForm.processResult,
          remark: this.processForm.remark
        }
        this.actionLoading = true
        processAlarm(payload).then(res => {
          if (!responseSuccess(res)) {
            this.$message.error(responseMessage(res, '处理失败'))
            return
          }
          this.$message.success(responseMessage(res, '处理成功'))
          this.processVisible = false
          this.refreshList()
          if (this.detailVisible && this.processRow) this.openDetail(this.processRow)
        }).catch(e => {
          console.warn(e)
          this.$message.error((e && e.message) || '处理失败')
        }).finally(() => {
          this.actionLoading = false
        })
      })
    },
    openWorkOrderDialog(row) {
      if (!this.canSubmitWorkOrder(row)) {
        this.$message.warning('该报警已有关联工单或不能提交工单')
        return
      }
      this.workOrderRow = row
      this.workOrderForm = {
        ...this.emptyWorkOrderForm(),
        id: row.id,
        faultLocation: this.roomText(row),
        faultDescription: `${this.roomText(row)} ${this.pointText(row)} ${this.alarmTypeLabel(row.alarmType)}，报警值：${this.valueText(this.alarmValue(row), row.unit)}，阈值：${this.valueText(row.thresholdValue, row.unit)}`
      }
      this.workOrderVisible = true
      this.$nextTick(() => this.$refs.workOrderForm && this.$refs.workOrderForm.clearValidate())
    },
    submitWorkOrderForm() {
      this.$refs.workOrderForm.validate(valid => {
        if (!valid) return
        const payload = {
          id: this.workOrderForm.id,
          faultDescription: this.workOrderForm.faultDescription,
          priority: this.workOrderForm.priority,
          remark: this.workOrderForm.remark,
          imagePath: this.workOrderForm.imagePath
        }
        this.actionLoading = true
        submitWorkOrder(payload).then(res => {
          if (!responseSuccess(res)) {
            this.$message.error(responseMessage(res, '提交维修工单失败'))
            return
          }
          const data = responseData(res)
          const orderNo = data.repairWorkOrderNo || data.repairOrderNo || data.workOrderNo
          this.$message.success(orderNo ? `提交成功：${orderNo}` : responseMessage(res, '提交成功'))
          this.workOrderVisible = false
          this.refreshList()
          if (this.detailVisible && this.workOrderRow) this.openDetail(this.workOrderRow)
        }).catch(e => {
          console.warn(e)
          this.$message.error((e && e.message) || '提交维修工单失败')
        }).finally(() => {
          this.actionLoading = false
        })
      })
    },
    openWorkOrder(row) {
      const orderNo = row.repairWorkOrderNo || row.workOrderNo || row.repairOrderId || row.workOrderId
      if (!orderNo) {
        this.$message.warning('暂无关联工单')
        return
      }
      this.$message.info(`关联工单：${orderNo}`)
    },
    splitText(value) {
      if (!value) return []
      return String(value).split(',').map(item => item.trim()).filter(Boolean)
    },
    imageList(value) {
      if (!value) return []
      if (Array.isArray(value)) return value.filter(Boolean)
      const text = String(value).trim()
      if (!text) return []
      if (text[0] === '[') {
        try {
          const arr = JSON.parse(text)
          return Array.isArray(arr) ? arr.filter(Boolean) : []
        } catch (e) {
          return []
        }
      }
      return text.split(',').map(item => item.trim()).filter(Boolean)
    },
    timelineType(node) {
      const status = this.normalizeStatus(node && (node.targetStatus || node.alertStatus || node.status))
      if (status === 'HANDLED') return 'success'
      if (status === 'CLOSED') return 'info'
      if (status === 'PROCESSING') return 'primary'
      return 'warning'
    }
  }
}
</script>

<style scoped lang="scss">
.environment-alarm-page {
  .alarm-summary {
    display: grid;
    grid-template-columns: repeat(5, minmax(120px, 1fr));
    gap: 8px;
    margin-bottom: 8px;
  }

  .summary-item {
    min-height: 58px;
    padding: 8px 12px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #fff;
  }

  .summary-label {
    line-height: 20px;
    font-size: 12px;
    color: #606266;
  }

  .summary-value {
    line-height: 24px;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    word-break: break-all;
  }

  .state-alert {
    margin-bottom: 8px;
  }

  .page-title {
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .danger-text {
    color: #f56c6c;
  }
}

.detail-body {
  min-height: 360px;
  padding: 0 8px 20px;
}

.detail-section {
  margin-bottom: 18px;
}

.section-title {
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid #ebeef5;
  border-left: 1px solid #ebeef5;
}

.detail-item {
  display: flex;
  min-height: 40px;
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}

.detail-item--wide {
  grid-column: span 2;
}

.detail-item__label {
  flex: 0 0 110px;
  padding: 10px 12px;
  background: #f5f7fa;
  color: #606266;
}

.detail-item__value {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  color: #303133;
  word-break: break-all;
}

.detail-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.timeline-node__title {
  font-weight: 600;
  color: #303133;
}

.timeline-node__meta {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.timeline-node__content {
  margin-top: 6px;
  color: #606266;
  line-height: 1.6;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.evidence-image {
  width: 88px;
  height: 88px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #909399;
  background: #f5f7fa;
}

@media (max-width: 1200px) {
  .environment-alarm-page {
    .alarm-summary {
      grid-template-columns: repeat(3, minmax(120px, 1fr));
    }
  }
}

@media (max-width: 640px) {
  .environment-alarm-page {
    .alarm-summary {
      grid-template-columns: 1fr;
    }
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-item--wide {
    grid-column: span 1;
  }

  .detail-actions {
    flex-wrap: wrap;
  }
}
</style>
